// ============================================================================
// Diccionario INGLÉS. Tipado contra es.ts: si falta una clave, el typecheck
// falla (no aparece un texto vacío en pantalla).
// ============================================================================
import type { Diccionario } from './es.ts';

export const en: Diccionario = {
  // --- App / shell -----------------------------------------------------------
  'app.subtitulo': 'Restaurant management system',
  'app.cargando': 'Loading…',
  'app.cargandoModulo': 'Loading module…',

  // --- Navigation ------------------------------------------------------------
  'nav.dashboard': 'Dashboard',
  'nav.kpis': 'Statistics',
  'nav.orders': 'Orders',
  'nav.reservations': 'Reservations',
  'nav.tables': 'Tables',
  'nav.menu': 'Menu',
  'nav.combos': 'Combos',
  'nav.inventory': 'Inventory',
  'nav.purchases': 'Costs & Expenses',
  'nav.activos': 'Assets & Supplies',
  'nav.aprobaciones': 'Approvals',
  'nav.misSolicitudes': 'My requests',
  'nav.contabilidad': 'Accounting',
  'nav.billing': 'Invoicing',
  'nav.payments': 'Payments',
  'nav.enviosMu': 'MU Deliveries',
  'nav.credenciales': 'Keys & credentials',
  'nav.terceros': 'Third parties',
  'nav.users': 'Users',
  'nav.reports': 'Reports',
  'nav.params': 'Settings',
  'nav.legalEntities': 'Legal Entities',
  'nav.cortesias': 'Comps',
  'nav.staff': 'Staff',
  'nav.ratings': 'Ratings',
  'nav.notificaciones': 'Notifications',
  'nav.features': 'Features',
  'nav.debug': 'Debugging',
  'nav.technicalDocs': 'Technical Docs',
  'nav.inicio': 'Home',
  'nav.mas': 'More',
  'nav.masOpciones': 'More options',

  // --- Sidebar ---------------------------------------------------------------
  'sidebar.cerrarMenu': 'Close menu',
  'sidebar.expandirMenu': 'Expand menu',
  'sidebar.minimizarMenu': 'Collapse menu',
  'sidebar.minimizar': 'Collapse',

  // --- Top bar ---------------------------------------------------------------
  'top.abrirMenu': 'Open menu',
  'top.notificaciones': 'Notifications',
  'top.sede': 'Branch: {sede}',
  'top.menuUsuario': 'User menu',
  'top.sinAlertas': 'No alerts. ✨',
  'top.reservasProximas': 'Upcoming reservations',
  'top.haceMin': '{n} min ago',
  'top.ahora': 'now',
  'top.enMin': 'in {n} min',
  'top.pers': 'guests',
  'top.descartarReserva': 'Dismiss reservation',
  'top.inventarioBajo': 'Inventory below minimum',
  'top.min': 'min',
  'top.descartar': 'Dismiss',
  'top.limpiarInventario': 'Clear inventory alerts',
  'top.verInventario': 'View inventory',
  'top.pantallaCompleta': 'Full screen',
  'top.salirPantallaCompleta': 'Exit full screen',
  'top.cambiarRol': 'Switch role (dev)',
  'top.ocultarCambiarRol': 'Hide Switch role',
  'top.cerrarSesion': 'Log out',
  'top.idioma': 'Language',
  'top.monedaChip': '{moneda} · no e-invoicing',
  'top.monedaChipTitulo': 'Currency {moneda}: DIAN electronic invoicing, Bold and MU Deliveries are disconnected. Change it in Features.',

  // --- Roles -----------------------------------------------------------------
  'rol.gerente_operativo': 'Operations Manager',
  'rol.gerente_administrativo': 'Administrative Manager',
  'rol.jefe_operaciones': 'Head of Operations',
  'rol.jefe_administrativo': 'Head of Administration',
  'rol.contador': 'Accounting',
  'rol.cajera': 'Cashier',
  'rol.mesero': 'Waiter',
  'rol.desarrollador': 'Developer',

  // --- Global search (top bar) -------------------------------------------------
  'busqueda.global': 'Global search',
  'busqueda.atajo': 'Search (⌘K)',
  'busqueda.boton': 'Search all of Salonika…',
  'busqueda.botonCorto': 'Search…',
  'busqueda.placeholder': 'Search dishes, items, tables, orders, reservations, invoices…',

  // --- Login -----------------------------------------------------------------
  'login.usuario': 'Username',
  'login.usuarioPlaceholder': 'Enter your username',
  'login.contrasena': 'Password',
  'login.contrasenaPlaceholder': 'Enter your password',
  'login.entrar': 'Sign in',

  // --- Currency / FED disconnected --------------------------------------------
  'regional.fedDesconectadaTitulo': 'Electronic invoicing disconnected',
  'regional.fedDesconectadaTexto': 'The configured currency is {moneda}. The DIAN only accepts invoices in COP, so FED is off and sales are recorded without an electronic invoice. Change it in Features (developer only).',

  // --- Features --------------------------------------------------------------
  'feat.soloDev': 'This module is only available to the developer role.',
  'feat.flagsActivos': 'Active flags',
  'feat.escaneosMes': 'Scans this month',
  'feat.intro': 'App flags, AI scan budget and scan history. (This used to live in Debugging.)',
  'feat.funcionesTitulo': 'App features',
  'feat.funcionesDesc': 'Turn modules on or off without redeploying. Users see the change after reloading.',
  'feat.cargando': 'Loading…',
  'feat.habilitado': 'Enabled',
  'feat.deshabilitado': 'Disabled',
  'feat.habilitadoMin': 'enabled',
  'feat.deshabilitadoMin': 'disabled',
  'feat.flagOk': '✓ {titulo}: {estado}. Users see it after reloading.',
  'feat.flagError': 'Could not save the flag',
  'feat.flag.scan.titulo': 'Scan invoices with AI',
  'feat.flag.scan.desc': '"Scan invoice" button in Costs & Expenses: reads invoices from a photo, classifies the expense and loads the inventory.',
  'feat.flag.staff.titulo': 'Staff module (shifts)',
  'feat.flag.staff.desc': 'Weekly shift planning for workers per branch. Shows up in the menu once enabled.',
  'feat.flag.ratings.titulo': 'Ratings module (post-payment QR)',
  'feat.flag.ratings.desc': 'QR code on every paid sale: the guest rates service, food and recommendation from their phone. Statistics only for superadmin and developer. Paid add-on.',
  'feat.flag.cortesias.titulo': 'Comps module',
  'feat.flag.cortesias.desc': '🎁 button at the table: items given away at $0, tracked (who/when/value), taken out of inventory and itemized on the sale. Includes the module with the history.',
  'feat.flag.combos.titulo': 'Combos module',
  'feat.flag.combos.desc': 'Bundle dishes with their own price (mig. 049). Off: the module disappears from the menu and combos cannot be created or edited. Existing combos stay on the menu.',
  'feat.presupuestoConfirmTitulo': 'Add +5 USD to the budget?',
  'feat.presupuestoConfirmTexto': 'It is added to the invoice scanning budget for the current month and logged under your name (so it can be billed to the client).',
  'feat.presupuestoConfirmBoton': 'Add +$5',
  'feat.presupuestoError': 'Could not grant the extra budget',
  'feat.presupuestoLinea': 'AI budget for the month ({mes}): ${gastado} of ${total} USD',
  'feat.presupuestoEscaneos': '{n} scans · base ${base}',
  'feat.presupuestoExtras': ' + extras ${extras}',
  'feat.presupuestoAgotado': ' · 🚫 EXHAUSTED: scanning blocked',
  'feat.presupuestoHabilitando': 'Adding…',
  'feat.historialTitulo': 'Scan history',
  'feat.historialDesc': 'Every attempt with its real checkpoint path (preparing → AI → analyzing → done), result and linked expense.',
  'feat.actualizar': 'Refresh',
  'feat.historialVacio': 'No scans recorded yet. (History starts with migration 029; earlier scans do not show up.)',
  'feat.col.fecha': 'Date',
  'feat.col.usuario': 'User',
  'feat.col.proveedor': 'Supplier read',
  'feat.col.total': 'Total',
  'feat.col.tipo': 'Type',
  'feat.col.lineas': 'Lines',
  'feat.col.estado': 'Status',
  'feat.col.detalle': 'Detail',
  'feat.col.costo': 'Cost',
  'feat.estado.procesando': 'Processing',
  'feat.estado.listo': 'Ready',
  'feat.estado.confirmado': 'Confirmed',
  'feat.estado.error': 'Error',
  'feat.estado.sinEstado': 'no status',
  'feat.gasto': 'expense #{id}',

  // --- Features: language & currency -----------------------------------------
  'feat.regionalTitulo': 'Language & currency',
  'feat.regionalDesc': 'Languages each user can pick from their user menu, and the currency for the whole installation.',
  'feat.idiomasTitulo': 'Enabled languages',
  'feat.idiomasDesc': 'Spanish is always on. Each user picks their language from the user menu in the top bar.',
  'feat.idiomasParcial': 'The whole interface is translated. Still in Spanish: messages sent by the server, documents printed for the customer or the DIAN (invoice, pre-bill) and data (dish names, categories, branches).',
  'feat.idiomaSiempre': 'Always on',
  'feat.idiomasGuardado': '✓ Enabled languages: {lista}.',
  'feat.monedaTitulo': 'Currency',
  'feat.monedaDesc': 'One currency for the whole installation. Only COP enables DIAN electronic invoicing (FED), Bold and MU Deliveries. Stored amounts are not converted.',
  'feat.monedaActual': 'Current',
  'feat.monedaConFE': 'With electronic invoicing',
  'feat.monedaSinFE': 'Without electronic invoicing',
  'feat.monedaConfirmTitulo': 'Change the currency to {moneda}?',
  'feat.monedaConfirmFuera': 'FED (DIAN electronic invoicing), Bold and MU Deliveries get disconnected. Sales are recorded without an electronic invoice.',
  'feat.monedaConfirmVuelta': 'FED, Bold and MU Deliveries reconnect with the configuration they already had.',
  'feat.monedaConfirmDatos': 'Amounts are NOT converted: there are {ventas} sales and {facturas} electronic invoices stored that will now be shown in {moneda}.',
  'feat.monedaConfirmBoton': 'Change to {moneda}',
  'feat.monedaCambiada': '✓ Currency changed to {moneda}. Reloading…',
  'feat.error': '✗ {mensaje}',
};
